import { apiClient } from '@/api/api'
import {
  ChainAbortedError,
  ChainFailedError,
  ChainTimeoutError,
  unwrapChainEnvelope,
  type UnwrappedChain
} from '@/api/utils/chainEnvelope'
import { sseService } from '@/api/sseClient'
import type { ChainCompletedEvent } from '@/types/sse'
import type { ShoppingListResponse } from '@/types/searchItemsTypes'
import axios from 'axios'

/** Overall ceiling — past this the user gets a retry, never an eternal spinner. */
const CHAIN_CEILING_MS = 3 * 60 * 1000
/** Slow confirmation cadence while the push channel is healthy. */
const SAFETY_POLL_MS = 25000
/** Fallback cadence when the push channel is down; grows to a cap. */
const FALLBACK_POLL_MIN_MS = 1000
const FALLBACK_POLL_MAX_MS = 8000
const FALLBACK_POLL_FACTOR = 1.5

export const chainStatus = async (chainId: string) => {
  return apiClient.get(`/shopping-list/status/${chainId}`)
}

/**
 * 404 means in-flight-or-expired per the backend contract, so it is a "keep
 * waiting" signal rather than an error. Anything else is a real failure.
 */
async function fetchChainState(
  chainId: string
): Promise<UnwrappedChain<ShoppingListResponse> | null> {
  try {
    const response = await chainStatus(chainId)
    return unwrapChainEnvelope<ShoppingListResponse>(response.data)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null
    }
    throw error
  }
}

/** Network failures and 5xx are worth retrying; other 4xx are definitive. */
function isTransientPollError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false
  const status = error.response?.status
  if (status === undefined) return true
  return status >= 500
}

function isChainCompletedFor(chainId: string, rawData: string): boolean {
  try {
    const parsed = JSON.parse(rawData) as Partial<ChainCompletedEvent>
    return parsed?.type === 'chain.completed' && parsed?.chain_id === chainId
  } catch {
    return false
  }
}

/**
 * Waits for a background chain to reach a terminal state.
 *
 * Push-first: a `chain.completed` event for this chain triggers an immediate
 * status read. Polling only exists so the flow still completes when the event
 * is missed or the stream is down — on reconnect we reconcile once, because
 * events delivered while disconnected are lost (at-most-once).
 */
export async function resolveChain(
  chainId: string,
  signal?: AbortSignal
): Promise<ShoppingListResponse> {
  const deadline = Date.now() + CHAIN_CEILING_MS
  let fallbackDelay = FALLBACK_POLL_MIN_MS

  return new Promise<ShoppingListResponse>((resolve, reject) => {
    let settled = false
    let timer: number | null = null
    let checking = false
    let recheckRequested = false

    const cleanup = () => {
      if (timer !== null) window.clearTimeout(timer)
      unsubscribeFrames()
      unsubscribeState()
      signal?.removeEventListener('abort', onAbort)
    }

    const finish = (fn: () => void) => {
      if (settled) return
      settled = true
      cleanup()
      fn()
    }

    const onAbort = () => {
      finish(() => reject(new ChainAbortedError()))
    }

    const scheduleNext = () => {
      if (settled) return
      if (timer !== null) window.clearTimeout(timer)

      if (Date.now() >= deadline) {
        finish(() => reject(new ChainTimeoutError(chainId)))
        return
      }

      // Healthy stream → slow confirmation. Down → tighten to backoff polling.
      // The backoff only grows when a poll actually fired (see check), so SSE
      // flapping cannot starve the fallback by advancing it without polling.
      const delay = sseService.isConnected ? SAFETY_POLL_MS : fallbackDelay

      timer = window.setTimeout(() => void check(), Math.min(delay, deadline - Date.now()))
    }

    const check = async () => {
      if (settled) return

      // Collapse overlapping triggers (event + timer) into one in-flight read.
      if (checking) {
        recheckRequested = true
        return
      }
      checking = true

      try {
        const state = await fetchChainState(chainId)

        if (state?.outcome === 'success' && state.result) {
          finish(() => resolve(state.result as ShoppingListResponse))
          return
        }

        if (state?.outcome === 'failed') {
          finish(() =>
            reject(new ChainFailedError(state.error ?? 'Search chain failed', chainId))
          )
          return
        }

        // Still pending — the next poll advances the fallback cadence.
        fallbackDelay = Math.min(fallbackDelay * FALLBACK_POLL_FACTOR, FALLBACK_POLL_MAX_MS)
      } catch (error) {
        // The poll is the safety net, not the source of truth: a 5xx or a network
        // blip must not kill a chain the backend is still working on. Only a
        // definitive client error (bad/rejected request) ends it.
        if (isTransientPollError(error)) {
          fallbackDelay = Math.min(fallbackDelay * FALLBACK_POLL_FACTOR, FALLBACK_POLL_MAX_MS)
          console.warn('Chain status poll failed, will retry:', error)
        } else {
          finish(() => reject(error))
          return
        }
      } finally {
        checking = false
      }

      // Re-entry skips the timer, so the ceiling is enforced here too.
      if (recheckRequested && Date.now() < deadline) {
        recheckRequested = false
        void check()
        return
      }
      recheckRequested = false

      scheduleNext()
    }

    const unsubscribeFrames = sseService.subscribe((frame) => {
      if (settled) return
      // The event rides the ordinary `notification` framing and is identified by
      // its payload, not by the SSE event name — do not filter on the name.
      if (!isChainCompletedFor(chainId, frame.data)) return
      void check()
    })

    const unsubscribeState = sseService.subscribeState((state) => {
      if (settled) return
      // Reconnected — an event may have fired while we were dark.
      if (state === 'connected') void check()
      else scheduleNext()
    })

    if (signal?.aborted) {
      onAbort()
      return
    }
    signal?.addEventListener('abort', onAbort)

    scheduleNext()
  })
}
